import { motion } from 'framer-motion';
import { useMemo, useEffect } from 'react';

interface TypingEffectProps {
    text: string;
    initialDelay?: number;
    timePerChar?: number;
    onComplete?: () => void;
}

export function TypingEffect({ text, initialDelay = 0, timePerChar = 0.01, onComplete }: TypingEffectProps) {
    // Generate a unique key based on the text content to force re-render when text changes
    const textKey = useMemo(() => Math.random().toString(36), [text]);

    // Calculate total animation duration and call onComplete when done
    useEffect(() => {
        if (!onComplete || text.length === 0) return;

        // Total time = delay of last character + animation duration
        const totalAnimationTime = (text.length - 1) * timePerChar + timePerChar;
        const timeoutMs = totalAnimationTime * 1000; // Convert to milliseconds

        const timer = setTimeout(() => {
            onComplete();
        }, timeoutMs);

        return () => clearTimeout(timer);
    }, [text, timePerChar, onComplete]);

    return (
        <>
            {text.split('').map((letter, index) => (
                <motion.span
                    key={`${textKey}-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: timePerChar, delay: initialDelay + index * timePerChar }}
                >
                    {letter}
                </motion.span>
            ))}
        </>
    );
}